import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Modal from 'components/Modal'
import NativeDateTimePicker from 'packages/eid-ui/DateTimePickers/NativeDateTimePicker'
import { Icon } from 'packages/eid-icons'
import moment from 'moment'
import Button from 'components/Button'

const DateTimePickerContainer = styled(Box)({
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
})

const DateTimePickerContent = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
})

const ButtonContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '16px',
})

// Custom styled component for the date input
const DateInputBox = styled(Box)({
    position: 'relative',
    width: '100%',
    height: '40px',
    border: '1px solid #d8dadd',
    borderRadius: '4px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    backgroundColor: '#fff',
    '&:hover': {
        borderColor: '#307fc1',
    },
})

const DateText = styled(Typography)({
    fontSize: '16px',
    color: '#333',
})

const HelperText = styled(Typography)({
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
    marginLeft: '12px',
})

/**
 * DateTimePickerPopup component with a simple custom input field
 */
const DateTimePickerPopup = ({
    open,
    onClose,
    title,
    value,
    onChange,
    minDate = '',
    maxDate = '',
    disabled = false,
    disabledTimeSlots = [],
    customTimeControl = false,
    todayBtnLabel = '',
    clearBtnLabel = '',
    pickerStyles = {},
    onSubmit,
    submitLabel = 'Submit',
    cancelLabel = 'Cancel',
    isLoading = false,
}) => {
    const [localValue, setLocalValue] = useState(value || '')
    const [formattedValue, setFormattedValue] = useState('')
    const datePickerRef = useRef(null)

    useEffect(() => {
        if (open && value) {
            setLocalValue(value)
            setFormattedValue(moment(value).format('YYYY-MM-DD hh:mm A'))
        }
    }, [open, value])

    const handleChange = (newValue) => {
        setLocalValue(newValue)
        setFormattedValue(moment(newValue).format('YYYY-MM-DD hh:mm A'))
        if (onChange) {
            onChange(newValue)
        }
    }

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit(localValue)
        }
        onClose()
    }

    const handleCancel = () => {
        setLocalValue(value) // Reset to original value
        onClose()
    }

    const handleBoxClick = () => {
        if (datePickerRef.current) {
            // Find the input element inside the NativeDateTimePicker
            const input = datePickerRef.current.querySelector(
                'input[type="datetime-local"]',
            )
            if (input) {
                if (typeof input.showPicker === 'function') {
                    // Modern browsers
                    input.showPicker()
                } else {
                    // Fallback for older browsers
                    input.focus()
                    input.click()
                }
            }
        }
    }

    return (
        <Modal open={open} onClose={onClose} title={title}>
            <DateTimePickerContainer>
                <DateTimePickerContent>
                    <DateInputBox onClick={handleBoxClick}>
                        <DateText>{formattedValue}</DateText>
                        <Icon name="Calendar" />
                    </DateInputBox>

                    <Box
                        ref={datePickerRef}
                        style={{ position: 'absolute', opacity: 0, zIndex: -1 }}
                    >
                        <NativeDateTimePicker
                            handleChange={handleChange}
                            value={localValue} // Format with 24h time
                            minDate={minDate}
                            maxDate={maxDate}
                            disabled={disabled}
                            disabledTimeSlots={disabledTimeSlots}
                            customTimeControl={customTimeControl}
                            todayBtnLabel={todayBtnLabel}
                            clearBtnLabel={clearBtnLabel}
                            pickerStyles={pickerStyles}
                        ></NativeDateTimePicker>
                    </Box>
                </DateTimePickerContent>
                <ButtonContainer>
                    <Button onClick={handleCancel}>{cancelLabel}</Button>
                    <Button.Submit loading={isLoading} onClick={handleSubmit}>
                        {submitLabel}
                    </Button.Submit>
                </ButtonContainer>
            </DateTimePickerContainer>
        </Modal>
    )
}

export default DateTimePickerPopup
